import type {
  FxSummary,
  IdmBreakdown,
  IdmBucket,
  Trade,
} from "./contracts";

export interface IdmSummary {
  rowCount: number;
  qBuy: IdmBucket;
  hBuy: IdmBucket;
  qSell: IdmBucket;
  hSell: IdmBucket;
  totalBuy: IdmBucket;
  totalSell: IdmBucket;
  /** Signed: positive is net bought, negative is net sold. */
  netMwh: number;
  /** Positive is net cash received; negative is net cash paid. */
  netCashFlow: number;
  /** Absolute net cash per net MWh; null for a flat position. */
  effectivePrice: number | null;
  /** Net cash flow when flat; null while the position is open. */
  realizedPnl: number | null;
}

export interface GroupedTradeSummary {
  bought: number;
  sold: number;
  net: number;
  buyVwap: number | null;
  sellVwap: number | null;
}

export function summarizeTradeRows(
  trades: readonly Pick<Trade, "side" | "quantityMwh" | "unitPrice">[]
): GroupedTradeSummary {
  let bought = 0;
  let sold = 0;
  let buyAbs = 0;
  let sellAbs = 0;
  let buyCash = 0;
  let sellCash = 0;

  for (const trade of trades) {
    const quantity = Number(trade.quantityMwh);
    const price = Number(trade.unitPrice);
    if (trade.side === "BUY") {
      bought += quantity;
      buyAbs += Math.abs(quantity);
      buyCash += Math.abs(quantity) * price;
    } else if (trade.side === "SELL") {
      sold += quantity;
      sellAbs += Math.abs(quantity);
      sellCash += Math.abs(quantity) * price;
    }
  }

  return {
    bought,
    sold,
    net: bought + sold,
    buyVwap: buyAbs > 0 ? buyCash / buyAbs : null,
    sellVwap: sellAbs > 0 ? sellCash / sellAbs : null,
  };
}

export function deriveIdmSummary(
  breakdown: IdmBreakdown,
  rowCount: number
): IdmSummary {
  const totalBuy = sumBuckets(breakdown.qBuy, breakdown.hBuy);
  const totalSell = sumBuckets(breakdown.qSell, breakdown.hSell);
  const netMwh = totalBuy.mwh - totalSell.mwh;
  const netCashFlow = totalSell.cash - totalBuy.cash;
  const isFlat = Math.abs(netMwh) < 1e-9;

  return {
    rowCount,
    qBuy: breakdown.qBuy,
    hBuy: breakdown.hBuy,
    qSell: breakdown.qSell,
    hSell: breakdown.hSell,
    totalBuy,
    totalSell,
    netMwh,
    netCashFlow,
    effectivePrice: isFlat ? null : Math.abs(netCashFlow) / Math.abs(netMwh),
    realizedPnl: isFlat ? netCashFlow : null,
  };
}

function sumBuckets(a: IdmBucket, b: IdmBucket): IdmBucket {
  const mwh = a.mwh + b.mwh;
  const cash = a.cash + b.cash;
  return { mwh, cash, vwap: mwh > 0 ? cash / mwh : null };
}

export function fxSummaryText(summary: FxSummary, prefix: string): string | null {
  if (summary.mode === "none") return null;
  if (summary.mode === "missing") {
    return `missing EUR/RON for ${summary.missingDates.slice(0, 3).join(", ")}${
      summary.missingDates.length > 3 ? ` +${summary.missingDates.length - 3}` : ""
    }`;
  }
  if (summary.mode === "single") {
    const rate = summary.rates[0];
    if (!rate) return null;
    return `${prefix} FX EUR/RON ${rate.rate.toFixed(4)} · fixing ${rate.asOfDate}`;
  }
  return `${prefix} daily EUR/RON (${summary.rates.length} dates)`;
}
